import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  AnalyticsPage,
  ANALYTICS_VIEWS,
  nextSearchForRange,
  nextSearchForView,
  readLastAnalyticsView,
  rememberAnalyticsView,
  resolveAnalyticsSearch,
  type AnalyticsSearch,
  type AnalyticsView,
} from '@/features/marketing';
import { DATE_PRESETS, type DatePreset } from '@/features/marketing/leads/date-range-select';

const PRESETS = new Set<DatePreset>(DATE_PRESETS);
const VIEWS = new Set<AnalyticsView>(ANALYTICS_VIEWS);

export const Route = createFileRoute('/_auth/$orgId/marketing/analytics')({
  validateSearch: (search: Record<string, unknown>): AnalyticsSearch => ({
    view: typeof search.view === 'string' && VIEWS.has(search.view as AnalyticsView) ? (search.view as AnalyticsView) : undefined,
    campaign: typeof search.campaign === 'string' && search.campaign ? search.campaign : undefined,
    range:
      typeof search.range === 'string' && PRESETS.has(search.range as DatePreset)
        ? (search.range as DatePreset)
        : undefined,
  }),
  component: AnalyticsRoute,
});

function AnalyticsRoute() {
  const { orgId } = Route.useParams();
  const search = Route.useSearch();
  // Read once on mount: a plain "Analytics" link reopens the view used last (else Campaigns).
  const [lastView] = useState(readLastAnalyticsView);
  const { view, range } = resolveAnalyticsSearch(search, lastView);
  const navigate = useNavigate({ from: '/$orgId/marketing/analytics' });
  useEffect(() => rememberAnalyticsView(view), [view]);

  return (
    <AnalyticsPage
      orgId={orgId}
      view={view}
      campaign={search.campaign}
      range={range}
      // Filter changes replace the history entry, so Back leaves Analytics instead of stepping through picks.
      onSelectView={(next) => navigate({ search: (previous) => nextSearchForView(previous, next), replace: true })}
      onSelectCampaign={(next) => navigate({ search: (previous) => ({ ...previous, campaign: next }) })}
      onSelectRange={(next) => navigate({ search: (previous) => nextSearchForRange(previous, next), replace: true })}
    />
  );
}
